import React from "react";
import { TFunction } from "i18next";
import { EyeIcon } from "@aureskonnect/react-ui";
import { Tooltip } from "@mui/material";
import classnames from "classnames";
import { useDispatch } from "react-redux";

import ErrorToast from "@components/Common/ErrorTost";
import { onKeyboardActivate } from "@helpers/a11y";
import { CESAR_LOYALTY } from "@constants/index";

import { AppDispatch } from "../../../../../../redux/store";
import {
    setNumberActive,
    setNumberArchived,
} from "../../../../../../redux/slices/fidelitySlice";

type Props = {
    row: FidelityRow;
    oneShop: boolean;
    isLoading: boolean;
    isModalOpened: boolean;
    numberActive: number;
    numberArchived: number;
    // `fidelityData.othersData.{dataDesignation,allFidelity}` est un sac
    // hétérogène géré par le parent ; pas modélisé ici.
    fidelityData: any;
    dataArchive: string[];
    setIsModalOpened: (v: boolean) => void;
    setFidelity: (row: FidelityRow) => void;
    setSelectedRows: (rows: FidelityRow[]) => void;
    activateFidelity: () => Promise<void>;
    t: TFunction;
};

type DecisionResult =
    | { kind: "error"; message: string }
    | { kind: "activate"; incrementCounters: boolean }
    | { kind: "activateAndToggleModal" }
    | { kind: "noop" };

function decideUnarchive(
    row: FidelityRow,
    oneShop: boolean,
    numberActive: number,
    fidelityData: any,
    dataArchive: string[],
    t: TFunction
): DecisionResult {
    const isCesar = row.fidelityChoiceIndex === CESAR_LOYALTY;

    if (isCesar && row.canActive === false && numberActive >= 1 && !oneShop) {
        return {
            kind: "error",
            message: t(
                "Attention, this loyalty group already exists in the franchise."
            ),
        };
    }

    const hasOtherDesignation =
        (!isCesar && fidelityData.othersData.dataDesignation.length >= 1) ||
        fidelityData.othersData.dataDesignation.some(
            (el: any) => el !== t("Softavera")
        );
    if (hasOtherDesignation && !oneShop) {
        return {
            kind: "error",
            message: t(
                "Attention, a franchise cannot have more than one setting fidelity"
            ),
        };
    }

    const isArchivedAndFranchise = dataArchive.includes(row.Id) && !oneShop;
    const isEmptyInOneShop =
        oneShop && fidelityData.othersData.dataDesignation.length === 0;

    if (isArchivedAndFranchise || isEmptyInOneShop) {
        return { kind: "activateAndToggleModal" };
    }

    if (oneShop) {
        const shopEntry = fidelityData.othersData.allFidelity?.data?.find(
            (el: any) => el.shopId === row.shopId
        );
        if (shopEntry?.subRows?.length > 0) {
            return {
                kind: "error",
                message: t(
                    "Attention,a shop cannot have more than one fidelity settings"
                ),
            };
        }
        return { kind: "activate", incrementCounters: true };
    }

    return { kind: "activate", incrementCounters: true };
}

export default function UnarchiveIconButton({
    row,
    oneShop,
    isLoading,
    isModalOpened,
    numberActive,
    numberArchived,
    fidelityData,
    dataArchive,
    setIsModalOpened,
    setFidelity,
    setSelectedRows,
    activateFidelity,
    t,
}: Props) {
    const dispatch = useDispatch<AppDispatch>();
    const tooltip =
        dataArchive.includes(row.Id) && oneShop ? "" : t("Unarchive");

    const handleClick = () => {
        const decision = decideUnarchive(
            row,
            oneShop,
            numberActive,
            fidelityData,
            dataArchive,
            t
        );

        if (decision.kind === "error") {
            ErrorToast(decision.message);
        } else if (decision.kind === "activateAndToggleModal") {
            activateFidelity();
            if (!oneShop) setIsModalOpened(!isModalOpened);
        } else if (decision.kind === "activate") {
            activateFidelity();
            if (decision.incrementCounters) {
                dispatch(setNumberActive(numberActive + 1));
                dispatch(setNumberArchived(numberArchived - 1));
            }
        }

        setFidelity(row);
        setSelectedRows([row]);
    };

    const className = classnames(
        "pointer__clz ml-3 cmn_icn_FRunarchive",
        {
            "not-allowed-icon__clz":
                (dataArchive.includes(row.Id) && oneShop) || isLoading,
        },
        oneShop ? "mr-2" : "ml-3"
    );

    return (
        <Tooltip title={tooltip}>
            <div
                data-testid={`fidelity-franchise-unarchive-icon-${row.Id}`}
                role="button"
                tabIndex={0}
                aria-label={tooltip || t("Unarchive")}
                onKeyDown={onKeyboardActivate(handleClick)}
            >
                <EyeIcon
                    height={25}
                    width={25}
                    style={{ cursor: "pointer" }}
                    onClick={handleClick}
                    className={className}
                />
            </div>
        </Tooltip>
    );
}
